import { formatCurrency } from '@/lib/format'
import type { Invoice, InvoiceEvent, InvoiceStatus } from '@/api/types'

export const INVOICE_STATUS_VALUES: InvoiceStatus[] = ['scheduled', 'unpaid', 'partially_paid', 'paid', 'voided', 'overdue']

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  scheduled: 'Planerad',
  unpaid: 'Obetald',
  partially_paid: 'Delbetald',
  paid: 'Betald',
  voided: 'Makulerad',
  overdue: 'Förfallen'
}

export const INVOICE_STATUS_STYLES: Record<InvoiceStatus, string> = {
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  unpaid: 'bg-blue-50 text-blue-700 border-blue-200',
  partially_paid: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  voided: 'bg-muted text-muted-foreground border-border',
  overdue: 'bg-red-50 text-red-700 border-red-200'
}

export interface InvoiceSummary {
  invoiced: number
  paid: number
  outstanding: number
  refundDue: number
}

// Det som ska betalas tillbaka till kunden efter en makulering och ännu inte markerats som gjort.
export function getOutstandingRefund(invoice: Pick<Invoice, 'refund_due' | 'refunded_at'>): number {
  if (invoice.refund_due === null || invoice.refunded_at !== null) {
    return 0
  }

  return Number(invoice.refund_due)
}

export function summarizeInvoices(invoices: Invoice[]): InvoiceSummary {
  const summary: InvoiceSummary = { invoiced: 0, paid: 0, outstanding: 0, refundDue: 0 }

  for (const invoice of invoices) {
    // Räknas före makuleringskontrollen: en återbetalning väntar bara på makulerade fakturor.
    summary.refundDue += getOutstandingRefund(invoice)

    if (invoice.status === 'voided') {
      continue
    }

    summary.invoiced += Number(invoice.amount)
    summary.paid += Number(invoice.paid_sum)
    summary.outstanding += Number(invoice.remaining_sum)
  }

  return summary
}

const UNNUMBERED_INVOICE_LABEL = 'Faktura (planerad)'

export function findInvoiceForShipment(invoices: Invoice[], shipmentId: number): Invoice | undefined {
  return invoices.find((invoice) => invoice.shipment_id === shipmentId)
}

export function formatInvoiceLabel(invoice: Invoice): string {
  if (invoice.invoice_number === null) {
    return UNNUMBERED_INVOICE_LABEL
  }

  return `Faktura ${invoice.invoice_number}`
}

const CREATED_EVENT_LABEL = 'Skapad'
const REGISTERED_EVENT_LABEL = 'Registrerad hos Capway'
const PAYMENT_EVENT_LABEL = 'Inbetalning'
const VOIDED_EVENT_LABEL = 'Makulerad'
const REFUND_REQUIRED_EVENT_LABEL = 'Återbetalning krävs'
const REFUNDED_EVENT_LABEL = 'Återbetald'
const UNKNOWN_STATUS_LABEL = '–'

function isInvoiceStatus(value: string): value is InvoiceStatus {
  return INVOICE_STATUS_VALUES.some((status) => status === value)
}

function formatStoredStatus(value: string | null): string {
  if (value === null) {
    return UNKNOWN_STATUS_LABEL
  }

  if (isInvoiceStatus(value)) {
    return INVOICE_STATUS_LABELS[value]
  }

  return value
}

export function formatInvoiceEvent(event: InvoiceEvent): string {
  if (event.kind === 'payment') {
    return `${PAYMENT_EVENT_LABEL} ${formatCurrency(Number(event.amount ?? '0'))}`
  }

  if (event.kind === 'status') {
    return `${formatStoredStatus(event.from_status)} → ${formatStoredStatus(event.to_status)}`
  }

  if (event.kind === 'registered') {
    return event.note === null ? REGISTERED_EVENT_LABEL : `${REGISTERED_EVENT_LABEL} som ${event.note}`
  }

  if (event.kind === 'voided') {
    return event.note === null ? VOIDED_EVENT_LABEL : `${VOIDED_EVENT_LABEL} – ${event.note}`
  }

  if (event.kind === 'refund_required') {
    return `${REFUND_REQUIRED_EVENT_LABEL} ${formatCurrency(Number(event.amount ?? '0'))}`
  }

  if (event.kind === 'refunded') {
    const label = `${REFUNDED_EVENT_LABEL} ${formatCurrency(Number(event.amount ?? '0'))}`

    return event.note === null ? label : `${label} – markerad av ${event.note}`
  }

  return CREATED_EVENT_LABEL
}

export function canVoidInvoice(invoice: Invoice): boolean {
  return invoice.status !== 'voided'
}

export function getVoidInvoiceConfirmMessage(invoice: Invoice): string {
  const paid = Number(invoice.paid_sum)
  const question = `Makulera ${formatInvoiceLabel(invoice)}? Den stängs hos Capway och ska inte betalas. Det går inte att ångra.`

  if (paid > 0) {
    return `${question} Kunden har betalat ${formatCurrency(paid)}, som ska betalas tillbaka för hand.`
  }

  return question
}
